// =============================================================================
// SimSense Platform — Friends Overlay Panel (OT-246: Steam-style persistent popup)
// =============================================================================

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../utils/api";
import { useToast } from "./Toast";

type FriendUser = {
  id: string;
  displayName: string | null;
  profilePictureUrl: string | null;
  tier: string | null;
};

type FriendRequest = {
  id: string;
  requesterId: string;
  addresseeId: string;
  status: string;
  createdAt: string;
  requester: FriendUser | null;
  addressee: FriendUser | null;
};

export function FriendsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [friends, setFriends] = useState<FriendUser[]>([]);
  const [incoming, setIncoming] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [friendsList, requestsData] = await Promise.all([
        api<FriendUser[]>("/api/friends/me"),
        api<{ incoming: FriendRequest[]; outgoing: FriendRequest[] }>("/api/friends/requests/me"),
      ]);
      setFriends(friendsList || []);
      setIncoming(requestsData?.incoming || []);
    } catch {
      // Silently fail — panel is supplementary
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const handleAccept = async (requestId: string) => {
    setActionLoading(requestId);
    try {
      await api(`/api/friends/requests/${requestId}/accept`, { method: "POST" });
      toast.success("Friend request accepted!");
      await fetchData();
    } catch {
      toast.error("Failed to accept request.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setActionLoading(requestId);
    try {
      await api(`/api/friends/requests/${requestId}/reject`, { method: "POST" });
      toast.success("Request declined.");
      await fetchData();
    } catch {
      toast.error("Failed to decline request.");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      {/* Backdrop */}
      {open && <div className="friends-panel-backdrop" onClick={onClose} />}

      {/* Panel */}
      <div className={`friends-panel${open ? " is-open" : ""}`}>
        <div className="friends-panel-header">
          <h3 className="friends-panel-title">Friends</h3>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Link
              to="/friends"
              onClick={onClose}
              className="friends-panel-manage-link"
            >Manage</Link>
            <button className="friends-panel-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="friends-panel-content">
          {/* Incoming Requests */}
          {incoming.length > 0 && (
            <div className="friends-panel-section">
              <div className="friends-panel-section-label">
                Pending Requests
                <span className="friends-panel-badge">{incoming.length}</span>
              </div>
              {incoming.map(req => (
                <div key={req.id} className="friends-panel-request">
                  <div className="friends-panel-user-info">
                    <Avatar user={req.requester} size={28} />
                    <span className="friends-panel-name">{req.requester?.displayName || "User"}</span>
                  </div>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      className="friends-panel-accept-btn"
                      onClick={() => handleAccept(req.id)}
                      disabled={actionLoading === req.id}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </button>
                    <button
                      className="friends-panel-reject-btn"
                      onClick={() => handleReject(req.id)}
                      disabled={actionLoading === req.id}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Friends List */}
          <div className="friends-panel-section">
            <div className="friends-panel-section-label">
              Friends
              <span className="friends-panel-count">{friends.length}</span>
            </div>
            {loading ? (
              <div className="friends-panel-empty">Loading...</div>
            ) : friends.length === 0 ? (
              <div className="friends-panel-empty">
                No friends yet.
                <Link to="/friends" onClick={onClose} style={{ color: "var(--accent)", marginLeft: "4px" }}>Add friends</Link>
              </div>
            ) : (
              friends.map(f => (
                <Link
                  key={f.id}
                  to={`/profile/${f.id}`}
                  className="friends-panel-friend"
                  onClick={onClose}
                >
                  <Avatar user={f} size={28} />
                  <span className="friends-panel-name">{f.displayName || "Anonymous"}</span>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function Avatar({ user, size = 28 }: { user: FriendUser | null; size?: number }) {
  if (!user) return null;
  const initials = (user.displayName || "?")[0].toUpperCase();
  return user.profilePictureUrl ? (
    <img
      src={user.profilePictureUrl}
      alt=""
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
    />
  ) : (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: "rgba(59,130,246,0.15)", color: "var(--accent)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, fontWeight: 700,
    }}>{initials}</div>
  );
}
